import { syncProductBadgeRootsFromSectionHtml } from '@theme/sync-product-badge';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, queryCheckedRadioByVariantId } from '@theme/utilities';

/**
 * @param {ParentNode | null | undefined} html
 * @returns {{ href: string; title: string } | null}
 */
function productTitleFromVariantSectionHtml(html) {
  if (!html) {
    return null;
  }

  const viewTitle = html.querySelector('.view-product-title a');
  if (viewTitle instanceof HTMLAnchorElement) {
    return {
      href: viewTitle.href,
      title: viewTitle.textContent?.trim() ?? '',
    };
  }

  const syncCard = html.querySelector('product-card[data-sync-product-title]');
  if (!(syncCard instanceof HTMLElement)) {
    return null;
  }
  const cardLink = syncCard.querySelector('[ref="productCardLink"]');
  if (!(cardLink instanceof HTMLAnchorElement)) {
    return null;
  }

  return {
    href: cardLink.href,
    title: syncCard.getAttribute('data-sync-product-title')?.trim() ?? '',
  };
}

/**
 * @param {Element | null | undefined} card
 * @returns {HTMLAnchorElement | null}
 */
function resolveProductCardLink(card) {
  if (!card) return null;

  if ('getProductCardLink' in card && typeof /** @type {import('./product-card').ProductCard} */ (card).getProductCardLink === 'function') {
    const link = /** @type {import('./product-card').ProductCard} */ (card).getProductCardLink();
    return link instanceof HTMLAnchorElement ? link : null;
  }

  const link = card.querySelector('[ref="productCardLink"]');
  return link instanceof HTMLAnchorElement ? link : null;
}

/**
 * @param {Element | null | undefined} card
 * @returns {string | null}
 */
function resolveSelectedVariantId(card) {
  if (!card) return null;

  if (
    'getSelectedVariantId' in card &&
    typeof /** @type {import('./product-card').ProductCard} */ (card).getSelectedVariantId === 'function'
  ) {
    return /** @type {import('./product-card').ProductCard} */ (card).getSelectedVariantId();
  }

  const quickAdd = card.querySelector('quick-add-component');
  const quickIdField = quickAdd?.querySelector('form input[name="id"]');
  if (quickIdField instanceof HTMLInputElement && !quickIdField.disabled && quickIdField.value) {
    return quickIdField.value;
  }

  const checked = card.querySelector('input[type="radio"]:checked[data-variant-id]');
  if (checked instanceof HTMLInputElement && checked.dataset.variantId) {
    return checked.dataset.variantId;
  }

  const link = resolveProductCardLink(card);
  if (link?.href) {
    try {
      return new URL(link.href, window.location.origin).searchParams.get('variant');
    } catch {
      return null;
    }
  }

  return null;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Document | null>>} */
  static #pageDocCache = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {boolean} */
  #openDialogWithKeyboard = false;

  get productPageUrl() {
    const productCard = this.closest('product-card');
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = resolveProductCardLink(productCard) || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    return resolveSelectedVariantId(this.closest('product-card'));
  }

  #isMobileModalProductCard() {
    return (
      this.dataset.mobileModalQuickAdd === 'true' &&
      this.dataset.inlineQuickAdd === 'true' &&
      isMobileBreakpoint() &&
      Boolean(this.closest('product-card'))
    );
  }

  #onMediaQueryLargeChange = () => {
    this.#closeQuickAddModal();
    const card = this.closest('product-card');
    if (card instanceof HTMLElement && this.dataset.mobileModalQuickAdd === 'true') {
      document.dispatchEvent(new CustomEvent('product-card:refresh-option-stacks', { detail: { card } }));
    }
  };

  #onVariantSelected = (/** @type {Event} */ e) => {
    this.#updateQuickAddButtonState(/** @type {import('@theme/events').VariantSelectedEvent} */ (e));
  };

  #onCardOptionInputChange = (/** @type {Event} */ e) => {
    if (this.#isMobileModalProductCard()) return;
    if (!(e.target instanceof HTMLInputElement) || e.target.type !== 'radio') return;
    if (!e.target.hasAttribute('data-input-id')) return;
    if (this.dataset.inlineQuickAdd !== 'true' || this.dataset.productOptionsCount !== '1') return;
    const card = this.closest('product-card');
    if (!card || e.target.closest('product-card') !== card) return;
    // Ignore events from a different (nested) quick-add or anything outside
    // this quick-add's own card-options subtree to avoid overwriting our
    // variant id with a sibling product context's selection.
    const sourceQuick = e.target.closest('quick-add-component');
    if (sourceQuick && sourceQuick !== this) return;
    const optionScope = e.target.closest(
      'swatches-variant-picker-component, variant-picker, product-swatches'
    );
    if (!optionScope || !card.contains(optionScope)) return;
    this.setAttribute('data-inline-swatch-picked', 'true');
    const vId = resolveSelectedVariantId(card);
    const idField = this.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    const atc = this.querySelector('add-to-cart-component');
    // Only flip to 'add' / enable the ATC when we actually have a variant id
    // to submit. If vId is null/empty we'd otherwise enable the button while
    // the form still carries a stale or empty id.
    if (vId) {
      this.setAttribute('data-quick-add-button', 'add');
      if (idField instanceof HTMLInputElement) {
        idField.value = vId;
        idField.disabled = false;
      }
      if (atc && 'enable' in atc) {
        (/** @type {{ enable: () => void }} */ (atc)).enable();
      }
    }
  };

  #syncInlineSwatchPickedFromDom = () => {
    if (this.#isMobileModalProductCard()) return;
    if (this.dataset.inlineQuickAdd !== 'true' || this.dataset.productOptionsCount !== '1') return;
    const card = this.closest('product-card');
    if (!card?.querySelector('input[type="radio"][data-input-id]:checked')) return;
    this.setAttribute('data-inline-swatch-picked', 'true');
    if (this.dataset.requireExplicitSwatch === 'true') {
      this.setAttribute('data-quick-add-button', 'add');
      const atc = this.querySelector('add-to-cart-component');
      if (atc && 'enable' in atc) {
        (/** @type {{ enable: () => void }} */ (atc)).enable();
      }
    }
  };

  #onQuickAddPointerDown = (/** @type {PointerEvent} */ event) => {
    if (event.button > 0) return;
    this.#openDialogWithKeyboard = false;
  };

  #onQuickAddKeyDown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    this.#openDialogWithKeyboard = true;
  };

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#cartUpdateAbortController;

    mediaQueryLarge.addEventListener('change', this.#onMediaQueryLargeChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    this.addEventListener('pointerdown', this.#onQuickAddPointerDown, { capture: true, signal });
    this.addEventListener('keydown', this.#onQuickAddKeyDown, { capture: true, signal });
    const variantEventTarget = this.closest('product-card') ?? document;
    variantEventTarget.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, {
      signal,
    });
    if (this.dataset.inlineQuickAdd === 'true' && this.closest('product-card')) {
      this.closest('product-card')?.addEventListener('change', this.#onCardOptionInputChange, {
        signal,
      });
    }
    if (this.dataset.inlineQuickAdd === 'true') {
      this.addEventListener('submit', this.#handleInlineProductFormSubmit);
    }
    requestAnimationFrame(() => {
      this.#syncInlineSwatchPickedFromDom();
      if (this.#isMobileModalProductCard()) {
        this.setAttribute('data-quick-add-button', 'choose');
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#onMediaQueryLargeChange);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.removeEventListener('submit', this.#handleInlineProductFormSubmit);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    QuickAddComponent.#pageDocCache.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const inCard = Boolean(this.closest('product-card'));
    const useMobileModal =
      inCard &&
      this.dataset.inlineQuickAdd === 'true' &&
      this.dataset.mobileModalQuickAdd === 'true' &&
      isMobileBreakpoint();

    if (this.dataset.inlineQuickAdd === 'true' && inCard && !useMobileModal) {
      this.#submitInlineProductForm();
      return;
    }

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /**
   * Copies the product card's selected variant into the inline add-to-cart hidden input.
   * Only enables that id for submission when the checked option is available (see
   * data-option-available / aria-disabled on the selected radio).
   *
   * @param {Element} productCard
   * @param {HTMLElement & { refs?: { variantId?: HTMLInputElement } }} formComponent
   */
  #syncInlineFormVariantIdFromCard(productCard, formComponent) {
    const variantIdField = formComponent.refs?.variantId;
    if (!variantIdField) return;

    const selectedId = resolveSelectedVariantId(productCard);
    if (!selectedId) return;

    const selectedRadio = queryCheckedRadioByVariantId(productCard, selectedId);
    const isUnavailable =
      selectedRadio?.dataset.optionAvailable === 'false' ||
      selectedRadio?.getAttribute('aria-disabled') === 'true';

    variantIdField.value = selectedId;
    variantIdField.disabled = Boolean(isUnavailable);
  }

  #submitInlineProductForm() {
    if (this.#isMobileModalProductCard()) return;
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const formComponent = /** @type {(HTMLElement & { refs?: { variantId?: HTMLInputElement } }) | null} */ (
      this.querySelector('product-form-component')
    );

    if (!(productCard && formComponent?.refs?.variantId)) {
      return;
    }

    this.#syncInlineFormVariantIdFromCard(productCard, formComponent);

    const { variantId } = formComponent.refs;
    if (!variantId?.value) {
      variantId?.reportValidity();
      return;
    }
    if (variantId.disabled) return;

    const form = /** @type {HTMLFormElement | null} */ (formComponent.querySelector('form'));
    if (!form) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    form.requestSubmit();
  }

  /**
   * Syncs the inline quick-add form variant ID before native form submit.
   * @param {SubmitEvent} event
   */
  #handleInlineProductFormSubmit = (event) => {
    if (this.#isMobileModalProductCard()) return;
    if (!(event.target instanceof HTMLFormElement)) return;

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    const formComponent = /** @type {(HTMLElement & { refs?: { variantId?: HTMLInputElement } }) | null} */ (
      event.target.closest('product-form-component')
    );

    if (!(productCard && formComponent?.refs?.variantId)) return;

    this.#syncInlineFormVariantIdFromCard(productCard, formComponent);

    const { variantId } = formComponent.refs;
    if (!variantId?.value) {
      variantId?.reportValidity();
      event.preventDefault();
      return;
    }
    if (variantId.disabled) {
      event.preventDefault();
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.dataset.dialogOpenedWithPointer = this.#openDialogWithKeyboard ? 'false' : 'true';
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const cached = QuickAddComponent.#pageDocCache.get(productPageUrl);
    if (cached) return cached;

    const maxRetries = 4;

    const promise = (async () => {
      try {
        for (let attempt = 0; attempt < maxRetries; attempt++) {
          const response = await fetch(productPageUrl);

          if (response.status === 429) {
            const retryAfterMs = Number(response.headers.get('Retry-After') ?? 0) * 1000 || (1000 * 2 ** attempt);
            await new Promise((resolve) => setTimeout(resolve, retryAfterMs));
            continue;
          }

          if (!response.ok) {
            throw new Error(`Failed to fetch product page: HTTP error £{response.status}`);
          }

          const responseText = await response.text();
          return new DOMParser().parseFromString(responseText, 'text/html');
        }
        QuickAddComponent.#pageDocCache.delete(productPageUrl);
        return null;
      } catch (error) {
        QuickAddComponent.#pageDocCache.delete(productPageUrl);
        throw error;
      }
    })();

    QuickAddComponent.#pageDocCache.set(productPageUrl, promise);
    return promise;
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productBadge = productDetails?.querySelector('[data-product-badge-root]');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      if (productBadge) {
        productHeader.appendChild(/** @type {Node} */ (productBadge.cloneNode(true)));
      }

      const titlePriceRow = document.createElement('div');
      titlePriceRow.classList.add('product-header__title-price');
      titlePriceRow.appendChild(productTitle);
      if (productPrice) {
        titlePriceRow.appendChild(productPrice);
      }
      productHeader.appendChild(titlePriceRow);
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    modalContent.replaceChildren(...Array.from(productGrid.children));

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    const card = this.closest('product-card');
    if (this.dataset.inlineQuickAdd === 'true') {
      if (this.dataset.productOptionsCount !== '1') {
        return;
      }
      if (this.#isMobileModalProductCard()) return;
      if (!(event.target instanceof HTMLElement)) {
        return;
      }
      if (event.target.closest('product-card') !== card) {
        return;
      }
      this.setAttribute('data-quick-add-button', 'add');
      this.setAttribute('data-inline-swatch-picked', 'true');
      const atc = this.querySelector('add-to-cart-component');
      if (atc && 'enable' in atc) {
        (/** @type {{ enable: () => void }} */ (atc)).enable();
      }
      return;
    }
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const card = this.closest('product-card');
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const picker = /** @type {import('@theme/variant-picker').default | null} */ (
      modalContent.querySelector('variant-picker')
    );
    const esc =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(selectedVariantId)
        : selectedVariantId;

    if (card instanceof Element) {
      for (const checked of card.querySelectorAll(
        'input[type="radio"]:checked[data-option-value-id]'
      )) {
        if (!(checked instanceof HTMLInputElement)) continue;
        const optionValueId = checked.dataset.optionValueId;
        if (!optionValueId) continue;
        const optionEsc =
          typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? CSS.escape(optionValueId)
            : optionValueId;
        const modalInput = modalContent.querySelector(`[data-option-value-id="£{optionEsc}"]`);
        if (modalInput instanceof HTMLInputElement && !modalInput.checked) {
          picker?.updateSelectedOption(modalInput);
        }
      }
    } else {
      const modalInput = modalContent.querySelector(`input[type="radio"][data-variant-id="£{esc}"]`);
      if (modalInput instanceof HTMLInputElement && !modalInput.checked) {
        picker?.updateSelectedOption(modalInput);
      }
    }

    const formComponent = modalContent.querySelector('product-form-component');
    const variantInput = formComponent?.querySelector('[ref="variantId"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = selectedVariantId;
      const matchedInput = modalContent.querySelector(`input[type="radio"][data-variant-id="£{esc}"]:checked`);
      const isUnavailable =
        matchedInput instanceof HTMLInputElement &&
        (matchedInput.dataset.optionAvailable === 'false' ||
          matchedInput.getAttribute('aria-disabled') === 'true');
      variantInput.disabled = Boolean(isUnavailable);
    }

    const notifyMe = formComponent?.querySelector('.notify-me-button');
    if (notifyMe instanceof HTMLButtonElement) {
      notifyMe.dataset.variantId = selectedVariantId;
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#onQuickAddVariantUpdate, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #onQuickAddVariantUpdate = (event) => {
    const html = event.detail.data.html;
    if (!html) return;

    const picked = productTitleFromVariantSectionHtml(html);
    if (picked) {
      const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
      const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
        this.querySelector('.product-header__title-price a')
      );

      for (const link of [viewMoreDetailsLink, mobileProductTitle]) {
        if (!(link instanceof HTMLAnchorElement)) {
          continue;
        }
        if (picked.href) {
          link.href = picked.href;
        }
        if (picked.title) {
          link.textContent = picked.title;
        }
      }
    }

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    /** @type {HTMLElement[]} */
    const roots = [];
    for (const el of modalContent.querySelectorAll('[data-product-badge-root]')) {
      if (el instanceof HTMLElement) roots.push(el);
    }
    if (roots.length) syncProductBadgeRootsFromSectionHtml(html, roots);
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `£{currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
